/**
 * CollectionDropdown.jsx
 * Universal dropdown for jayram collections via backend API.
 * Uses fetch() with explicit API base URL to avoid dev-server HTML responses.
 */

// Imports React for building UI component, as well as hooks for state/effect management
import React, { useEffect, useState } from "react";
// Imports app-specific store for reading app name context
import { useAppStore } from "../../store/useAppStore";
// Imports logger utility to monitor user actions and errors
import { logger } from "../../utils/logger";

// Resolve API base URL robustly (Vite, CRA, window var, fallback)
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_API_BASE) ||
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_BASE) ||
  (typeof window !== "undefined" && window.__API_BASE__) ||
  "http://localhost:5000";

// Fixed by Claude on 2025-11-10 - Added returnFullObject support for complete JSON fetching
// Dropdown component for selecting items from a backend collection
const CollectionDropdown = ({
  appName: propAppName = null,
  dbName = "jayram",
  collectionType = "pages",
  label = "Select Item",
  currentItem = null,
  value = "",
  onChange = () => {},
  labelKey = "name",
  valueKey = "id",
  disabled = false,
  style = {},
  returnFullObject = false, // NEW: If true, onChange receives full object instead of just value
}) => {
  // Reads app name from store if not provided as prop
  const { appName: storeAppName } = useAppStore();
  const effectiveAppName = propAppName || storeAppName;

  // List of dropdown items and status states
  const [items, setItems] = useState([]);
  const [fullObjects, setFullObjects] = useState([]); // NEW: Store complete JSON objects
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Log: Dropdown component is being rendered for a given app/collection
  logger.log(`📂 [CollectionDropdown] Component rendered for app: ${effectiveAppName}, collection: ${collectionType}`);

  // Loads data from backend when settings change
  useEffect(() => {
    // Helps prevent state updates if user leaves while loading
    let abort = false;

    // This function fetches collection items from backend API
    const loadData = async () => {
      if (!effectiveAppName || !collectionType) {
        logger.warn("⚠️ [CollectionDropdown] Missing appName or collectionType");
        setError("Missing appName or collectionType");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        // Compose endpoint for backend request
        const url = `${API_BASE}/api/collection/${encodeURIComponent(dbName)}/${encodeURIComponent(
          effectiveAppName
        )}/${encodeURIComponent(collectionType)}`;

        // Log: Attempting to fetch backend data
        logger.log(`📂 [CollectionDropdown] Fetching data from: ${url}`);

        // Make the network request
        const resp = await fetch(url, { headers: { "Content-Type": "application/json" } });

        // Check if response is error or not JSON
        const ct = resp.headers.get("content-type") || "";
        if (!resp.ok) {
          logger.error(`[CollectionDropdown] HTTP error status: ${resp.status}`);
          console.error("[CollectionDropdown] HTTP error:", resp.status, await resp.text());
          if (!abort) setError("Failed to fetch data.");
          return;
        }
        if (!ct.toLowerCase().includes("application/json")) {
          logger.error("[CollectionDropdown] Received non-JSON response. content-type=" + ct);
          console.error("[CollectionDropdown] Non-JSON response. content-type=", ct);
          if (!abort) setError("Failed to fetch data.");
          return;
        }

        // Parse the JSON body, normalize it into array
        const data = await resp.json();
        const normalized = Array.isArray(data) ? data : [];

        // Fixed by Claude on 2025-11-10 - Store full objects for returnFullObject support
        // Store complete JSON objects
        if (!abort) setFullObjects(normalized);

        // Map response into label/value pairs for dropdown
        const mapped = normalized
          .map((it) => {
            const label =
              it[labelKey] ?? it.pageName ?? it.name ?? it.title ?? String(it.id ?? "");
            const val = it[valueKey] ?? it.id ?? it.pageName ?? it.name ?? label;
            return { label, value: val, fullObject: it }; // Store reference to full object
          })
          .filter((opt) => {
            if (!currentItem) return true;
            try {
              return String(opt.label).toLowerCase() !== String(currentItem).toLowerCase();
            } catch {
              return true;
            }
          });

        // Log: Items successfully loaded and mapped
        logger.log(`📊 [CollectionDropdown] Loaded ${mapped.length} items`);

        // Update state unless component was aborted/unmounted
        if (!abort) setItems(mapped);
      } catch (err) {
        // Log: Network or backend error during fetch
        logger.error(`[CollectionDropdown] fetch error: ${err.message}`);
        console.error("[CollectionDropdown] fetch error:", err);
        if (!abort) setError("Failed to fetch data.");
      } finally {
        if (!abort) setLoading(false);
      }
    };

    // Start loading data
    loadData();
    // Clean up on unmount so async responses don't overwrite state after navigation
    return () => {
      abort = true;
    };
  }, [dbName, effectiveAppName, collectionType, currentItem, labelKey, valueKey]);

  // Shows loading state when fetching from backend
  if (loading)
    return <div style={{ fontSize: 13, color: "#6B7280", marginTop: 4 }}>Loading {collectionType}…</div>;

  // Renders error if there was a problem loading items
  if (error)
    return (
      <div style={{ fontSize: 12, color: "#DC2626", marginTop: 4 }}>
        ⚠️ {error}
      </div>
    );

  // Renders the dropdown select form
  return (
    <div style={{ ...style }}>
      {/* Dropdown label for clarity */}
      {label && (
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: "#374151",
            marginBottom: 4,
          }}
        >
          {label}
        </label>
      )}

      {/* Main select element */}
      <select
        value={value}
        disabled={disabled || items.length === 0}
        // Fixed by Claude on 2025-11-10 - Return full object when returnFullObject=true
        // Handles dropdown selection change event
        onChange={(e) => {
          const selectedValue = e.target.value;
          logger.log(`[CollectionDropdown] Item selected: ${selectedValue}`);
          
          if (returnFullObject) {
            // Find and return the complete JSON object
            const selectedItem = items.find(item => String(item.value) === String(selectedValue));
            if (selectedItem && selectedItem.fullObject) {
              logger.log(`[CollectionDropdown] Returning full object for: ${selectedValue}`);
              onChange(selectedItem.fullObject);
            } else {
              onChange(selectedValue); // Fallback to value if object not found
            }
          } else {
            // Default behavior: return just the value
            onChange(selectedValue);
          }
        }}
        style={{
          width: "100%",
          padding: "8px 10px",
          border: "1px solid #D1D5DB",
          borderRadius: "6px",
          fontSize: "13px",
          backgroundColor: disabled ? "#F9FAFB" : "#FFFFFF",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <option value="">
          {items.length === 0 ? `No ${collectionType} available` : `-- Select ${collectionType.slice(0, -1)} --`}
        </option>
        {items.map((p) => (
          <option key={`${p.value}`} value={p.value}>
            {p.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// Makes dropdown component available for other screens
// End of fix by Claude on 2025-11-10 - CollectionDropdown now supports returnFullObject for complete JSON
export default CollectionDropdown;
